<template>
  <div
    class="free-gift-callout"
    :class="{ 'free-gift-callout--unlocked': isUnlocked }"
    :style="crossfadeVars"
  >
    <!-- Both headings occupy one grid cell so the container is always as tall as the taller of the
         two. Crossfading absolutely-positioned headings would collapse the container's height and
         make the whole callout jump at the exact moment it is meant to be celebrating. -->
    <div class="free-gift-callout__headings">
      <!-- eslint-disable-next-line vue/no-v-html -- Contentful-authored heading (incl. its icon), markdown-rendered in useFreeGift -->
      <div
        class="free-gift-callout__heading"
        :aria-hidden="isUnlocked"
        v-html="headingHtml"
      />
      <!-- eslint-disable-next-line vue/no-v-html -- in-code copy by default; a prop so it can become authored without a component change -->
      <div
        class="free-gift-callout__heading free-gift-callout__heading--unlocked"
        :aria-hidden="!isUnlocked"
        v-html="unlockedHeadingHtml"
      />
    </div>

    <div class="free-gift-callout__card">
      <div
        v-if="detailImageUrl || resolvedUnlockedImageUrl"
        class="free-gift-callout__media"
      >
        <img
          v-if="detailImageUrl"
          :src="detailImageUrl"
          alt=""
          class="free-gift-callout__image"
        />
        <img
          v-if="resolvedUnlockedImageUrl"
          :src="resolvedUnlockedImageUrl"
          alt=""
          class="free-gift-callout__image free-gift-callout__image--unlocked"
        />
        <!-- Board `15999:29170`, annotated "Updated to show lock Icon on image and overlay". Fades
             out as part of the 2,000ms beat, which is why it is an overlay rather than a `v-if`. -->
        <div
          class="free-gift-callout__lock-overlay"
          aria-hidden="true"
        >
          <FontAwesomeIcon
            :icon="appIcons.faLockKeyhole"
            class="free-gift-callout__lock-glyph"
          />
        </div>
      </div>
      <!-- The two detail states are stacked in one grid cell for the same reason as the headings, and
           because they differ structurally: locked is sprite-then-name, unlocked is name-then-copy. -->
      <div class="free-gift-callout__details">
        <div
          class="free-gift-callout__detail-state"
          :aria-hidden="isUnlocked"
        >
          <div class="free-gift-callout__locked">
            <FontAwesomeIcon
              :icon="appIcons.faLockKeyhole"
              class="free-gift-callout__locked-icon"
            />
            <span>{{ cartSidebarContent.freeGiftLockedLabel }}</span>
          </div>
          <div
            v-if="rewardName"
            class="free-gift-callout__item-name"
          >
            {{ rewardName }}
          </div>
        </div>
        <div
          class="free-gift-callout__detail-state free-gift-callout__detail-state--unlocked"
          :aria-hidden="!isUnlocked"
        >
          <div
            v-if="resolvedUnlockedRewardName"
            class="free-gift-callout__item-name"
          >
            {{ resolvedUnlockedRewardName }}
          </div>
          <div class="free-gift-callout__added">{{ addedCopy }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

import useAppIcons from '@/composables/appIcons'
import {
  CELEBRATION_ENTER_RISE_PX,
  CELEBRATION_EXIT_LIFT_MS,
  CELEBRATION_EXIT_LIFT_PX,
  CELEBRATION_FADE_MS,
  CELEBRATION_FADE_OUT_MS
} from '@/lib/rewardSequenceTimings'

import { cartSidebarContent } from '@/content/cartSidebar'

/**
 * Presentational Free Gift callout: a gradient container with a heading, then a preview card (detail
 * image left, status + reward name right). Props-driven, no store access — `AppCartSidebar` feeds it
 * from `useFreeGift` and the debug harness feeds it fixtures.
 *
 * **It draws two states, and they are the two halves of one transition.** Locked (Figma `15999:29170`
 * / `15999:29431`) and unlocked (`15927:54430`) are the from- and to-frames of the sequence's 2,000ms
 * beat, which the spec describes as every element crossfading together over
 * {@link CELEBRATION_FADE_MS}. That is why both states ship in one change rather than the locked half
 * arriving with Styling: shipping one side of a transition whose other side does not exist leaves the
 * animation work to retrofit it, and leaves the resting state looking like a bug in the meantime.
 *
 * | | Locked | Unlocked |
 * |---|---|---|
 * | Heading | authored, `##amount##` substituted | "Score! You Unlocked a Free Gift" |
 * | Image | `rgba(0,0,0,0.2)` overlay + centred white lock glyph | clean |
 * | Details | "Locked" sprite + reward name | reward name + "Added to your order!" |
 *
 * **Both states are always in the DOM**, stacked in a grid cell and crossfaded on opacity. A `v-if`
 * swap cannot cross-fade, and it would resize the container mid-celebration. The inactive state is
 * `aria-hidden`, since an opacity of 0 hides nothing from a screen reader.
 *
 * The component only renders the state it is told to. Nothing here watches the cart or the sequence —
 * `isUnlocked` is driven by the sequence machine's phase at A2a.
 */
export default defineComponent({
  name: 'FreeGiftCallout',
  components: { FontAwesomeIcon },
  props: {
    /** Locked-state heading. Pre-rendered markdown (emphasis as `<strong>`). */
    headingHtml: {
      type: String,
      required: true
    },
    /** Unlocked-state heading. Defaults to the board's copy; a prop so it can become authored. */
    unlockedHeadingHtml: {
      type: String,
      default: cartSidebarContent.freeGiftUnlockedHeading
    },
    /**
     * The reward's name — the granted package's name, or the promotion's retail name. Shown in
     * **both** states, which is why it is no longer called `lockedItemName`. May be empty.
     */
    rewardName: {
      type: String,
      default: ''
    },
    /** Resolved detail-image URL (left of the card), when authored. */
    detailImageUrl: {
      type: String,
      default: undefined
    },
    /**
     * The reward named by the **unlocked** half, when it differs from the locked half's.
     *
     * They differ on a multi-tier crossing, and only then: the locked frame is the reward the customer
     * was working toward when they acted, and the unlocked frame is the one they actually won — which
     * is the *last* tier crossed, not the first. Two subjects, which is what a crossfade is for.
     * Defaults to the locked reward, so a single-tier crossing needs neither of these.
     */
    unlockedRewardName: {
      type: String,
      default: undefined
    },
    /** The unlocked half's image, for the same reason. Defaults to the locked one. */
    unlockedDetailImageUrl: {
      type: String,
      default: undefined
    },
    /** Line replacing the "Locked" sprite once unlocked. */
    addedCopy: {
      type: String,
      default: cartSidebarContent.freeGiftAddedToOrder
    },
    /** Drives the crossfade. The sequence machine owns this at A2a; it is the resting state until then. */
    isUnlocked: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    return { ...useAppIcons(), cartSidebarContent }
  },
  computed: {
    /** Falls back to the locked half, so the common single-tier case passes nothing extra. */
    resolvedUnlockedRewardName(): string {
      return this.unlockedRewardName ?? this.rewardName
    },
    resolvedUnlockedImageUrl(): string | undefined {
      return this.unlockedDetailImageUrl ?? this.detailImageUrl
    },
    /** Crossfade timings and offsets, handed to SCSS so the spec's numbers keep one source. */
    crossfadeVars(): Record<string, string> {
      return {
        '--free-gift-fade-in': `${CELEBRATION_FADE_MS}ms`,
        '--free-gift-fade-out': `${CELEBRATION_FADE_OUT_MS}ms`,
        '--free-gift-lift-ms': `${CELEBRATION_EXIT_LIFT_MS}ms`,
        '--free-gift-rise': `${CELEBRATION_ENTER_RISE_PX}px`,
        '--free-gift-lift': `${CELEBRATION_EXIT_LIFT_PX}px`
      }
    }
  }
})
</script>

<style scoped lang="scss">
// Mobile-first per Figma `10192:197531`; ≥md overrides match desktop `10192:196823`.
.free-gift-callout {
  display: flex;
  flex-direction: column;
  padding: 1.125rem 1.5rem; // 18px / 24px (mobile)
  border-radius: 16px;
  // Brand primary gradient (left → right); falls back to the literal stops if the token is absent.
  background: var(--gradient-horizontal, linear-gradient(90deg, #514c9a 0%, #ad4091 100%));
  color: #fff;

  @include media-breakpoint-up(md) {
    padding: 1.5rem 2rem; // 24px / 32px (desktop)
  }
}

// One grid cell, two headings — see the template note. The container takes the taller one's height,
// so the crossfade cannot change the callout's size.
.free-gift-callout__headings {
  display: grid;
  margin-bottom: 1rem;

  > * {
    grid-area: 1 / 1;
  }

  @include media-breakpoint-up(md) {
    margin-bottom: 1.25rem;
  }
}

// Heading is markdown HTML (icon + copy from Contentful): mobile renders regular weight (only
// `**FREE Gift**` bold); desktop is all-bold (h6) via the container weight, which also bolds the
// `<strong>` span.
// Enter/exit are asymmetric, per the spec: 300ms in against 200ms out, content rising 12px into place
// and lifting 8px away over 350ms. Each state carries the transition for *becoming* that state, which
// is how one class toggle drives both directions without a JS-supplied direction flag.
//
// **Known simplification.** A state's hidden position can only be one thing, so it is the enter
// offset (+12px below). Reversing the crossfade — which only the harness's manual toggle can do, never
// the sequence — therefore sends content back down rather than up. Correct in the direction that
// ships; recorded so the harness's reverse toggle isn't read as a bug.
.free-gift-callout__heading {
  font-weight: 400;
  transition:
    opacity var(--free-gift-fade-in) ease,
    transform var(--free-gift-fade-in) ease;

  // Pin the rendered markdown paragraph's size directly — it's injected via v-html, so it can't be
  // relied on to inherit the container font-size. 18px mobile, 22px (h6) desktop.
  :deep(p) {
    margin: 0;
    font-size: 1.125rem; // 18px
    line-height: 1.75rem;
  }

  @include media-breakpoint-up(md) {
    font-weight: 700; // desktop h6 is all-bold; cascades to the paragraph + its `<strong>`

    :deep(p) {
      font-size: 1.375rem; // 22px
      line-height: 1.2;
    }
  }

  // The unlocked heading is plain text rather than a rendered paragraph, so it needs the type the
  // `:deep(p)` rules above give the locked one.
  &--unlocked {
    font-size: 1.125rem; // 18px
    line-height: 1.75rem;
    opacity: 0;
    transform: translateY(var(--free-gift-rise));
    transition:
      opacity var(--free-gift-fade-out) ease,
      transform var(--free-gift-lift-ms) ease;

    @include media-breakpoint-up(md) {
      font-size: 1.375rem; // 22px
      line-height: 1.2;
    }
  }
}

// White preview card (Figma "Details Container"): image left, white panel right.
.free-gift-callout__card {
  display: flex;
  align-items: stretch;
  height: 10.25rem; // 164px (mobile)
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 8px 23px rgba(0, 0, 0, 0.06);

  @include media-breakpoint-up(md) {
    height: 8.5rem; // 136px (desktop)
  }
}

// Wraps the images so the lock overlay has something to be absolute against, and stacks them so the
// locked and unlocked rewards can crossfade when they are different rewards.
.free-gift-callout__media {
  position: relative;
  display: grid;
  flex-shrink: 0;
  width: 7.5rem; // 120px (mobile)

  @include media-breakpoint-up(md) {
    width: 12.8125rem; // 205px (desktop)
  }
}

.free-gift-callout__image {
  grid-area: 1 / 1;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;

  &--unlocked {
    opacity: 0;
    transition: opacity var(--free-gift-fade-out) ease;
  }
}

// Figma `15999:29170` — a flat 20% scrim with a centred white lock glyph at 36px.
.free-gift-callout__lock-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.2);
  transition: opacity var(--free-gift-fade-out) ease;
}

.free-gift-callout__lock-glyph {
  font-size: 2.25rem; // 36px
  color: #fff;
}

.free-gift-callout__details {
  display: grid;
  flex: 1 1 0;
  min-width: 0;
  align-items: center;
  padding: 1rem;
  background: #fff;

  > * {
    grid-area: 1 / 1;
  }
}

.free-gift-callout__detail-state {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 0;
  transition:
    opacity var(--free-gift-fade-in) ease,
    transform var(--free-gift-fade-in) ease;

  &--unlocked {
    opacity: 0;
    transform: translateY(var(--free-gift-rise));
    transition:
      opacity var(--free-gift-fade-out) ease,
      transform var(--free-gift-lift-ms) ease;
  }
}

// Figma sprite: `#eee` pill, lock glyph 12px + "Locked" 18/28, both `#929292`.
.free-gift-callout__locked {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  background: #eee;
  color: #929292;
  font-weight: 700;
  font-size: 1.125rem;
  line-height: 1.75rem;
}

.free-gift-callout__locked-icon {
  font-size: 0.75rem;
}

.free-gift-callout__item-name {
  color: #000;
  font-weight: 700;
  font-size: 1.125rem;
  line-height: 1.75rem;
}

// No board frame draws this line — see `cartSidebarContent.freeGiftAddedToOrder`. The colour borrows the FREE pill's
// `#3a7f3f` (`16066:33312`, S4) as the nearest reward-positive text colour the board does define;
// the type matches the reward name it sits under. Both await a design confirmation.
.free-gift-callout__added {
  color: #3a7f3f;
  font-weight: 700;
  font-size: 1.125rem;
  line-height: 1.75rem;
}

// The crossfade itself. Everything above defines the two frames; this is the only rule that knows
// which one is showing, so the beat is a single class toggle rather than a spread of conditionals.
.free-gift-callout--unlocked {
  // Outgoing: the quicker fade, lifting away over the longer 350ms — so it has finished fading before
  // it has finished moving, and the movement is what carries the eye out.
  .free-gift-callout__heading,
  .free-gift-callout__detail-state {
    opacity: 0;
    transform: translateY(var(--free-gift-lift));
    transition:
      opacity var(--free-gift-fade-out) ease,
      transform var(--free-gift-lift-ms) ease;
  }

  // Incoming: the longer fade, rising from below into place.
  .free-gift-callout__heading--unlocked,
  .free-gift-callout__detail-state--unlocked {
    opacity: 1;
    transform: none;
    transition:
      opacity var(--free-gift-fade-in) ease,
      transform var(--free-gift-fade-in) ease;
  }

  // Fade only — the JIRA spec lists the lock overlay as fading out and says nothing about movement,
  // and a scrim that slides off its own image would read as a layer coming loose.
  .free-gift-callout__lock-overlay {
    opacity: 0;
  }

  .free-gift-callout__image--unlocked {
    opacity: 1;
    transition: opacity var(--free-gift-fade-in) ease;
  }
}

// The crossfade is the celebration; without motion the two states simply swap. A5 routes the machine
// through `settle()`, which lands on the same end state this reaches instantly.
@media (prefers-reduced-motion: reduce) {
  .free-gift-callout__heading,
  .free-gift-callout__detail-state,
  .free-gift-callout__lock-overlay {
    transition: none;
    transform: none;
  }
}
</style>
